import React, { useEffect, useState, ReactNode } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@systemeio/icons'
import { twJoin } from 'tailwind-merge'
import type { PagerType } from './paginate'
import paginate from './paginate'

export type PaginationData<T> = {
  total: number
  data: T
}

type PaginationProps = {
  total: number
  currentPage: number
  pageSize?: number
  onChangePage: (page: number) => void
}

function Pagination({
  total,
  currentPage,
  pageSize = 12,
  onChangePage,
}: PaginationProps): ReactNode {
  const [pager, setPager] = useState<PagerType>({
    totalItems: 0,
    currentPage: 1,
    pageSize,
    totalPages: 0,
    startPage: 1,
    endPage: 1,
    startIndex: 0,
    endIndex: 0,
    pages: [],
  })

  useEffect(() => {
    setPager(paginate(total, currentPage, pageSize, 4))
    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    if (total > 0 && currentPage > totalPages) {
      onChangePage(totalPages)
    }
  }, [total, currentPage, pageSize, onChangePage])

  function selectPage(page: number): () => void {
    if (page > 0 && page <= pager.totalPages) {
      return () => {
        onChangePage(page)
      }
    }
    return () => {}
  }

  const leftPaginationDotsNeeded = pager.currentPage >= pager.pageSize - 2
  const rightPaginationDotsNeeded = pager.currentPage <= pager.totalPages - pager.pageSize + 3

  return (
    <div className="flex items-center justify-between gap-1">
      {pager.currentPage !== pager.startPage && (
        <PaginationItem key="pagination-prev" onClick={selectPage(pager.currentPage - 1)}>
          <ArrowLeftIcon className="size-5 text-text-primary" />
        </PaginationItem>
      )}
      {pager.pages.map((page, index) => (
        <React.Fragment key={index}>
          <PaginationItem
            onClick={selectPage(page)}
            active={pager.currentPage === page}
            key={index}
          >
            {page}
          </PaginationItem>
          {index === 0 && leftPaginationDotsNeeded && (
            <PaginationItem key="left-dots">...</PaginationItem>
          )}
          {index === pager.pages.length - 2 && rightPaginationDotsNeeded && (
            <PaginationItem key="right-dots">...</PaginationItem>
          )}
        </React.Fragment>
      ))}
      {pager.currentPage !== pager.endPage && (
        <PaginationItem key="pagination-next" onClick={selectPage(pager.currentPage + 1)}>
          <ArrowRightIcon className="size-5 text-text-primary" />
        </PaginationItem>
      )}
    </div>
  )
}

export interface PaginationItemProps
  extends React.HTMLAttributes<HTMLDivElement>, React.PropsWithChildren {
  active?: boolean
}

function PaginationItem({ children, active, ...props }: PaginationItemProps): ReactNode {
  return (
    <div
      className={twJoin(
        'flex size-10 items-center justify-center rounded-full p-2 font-medium',
        active
          ? 'cursor-default bg-bg-secondary text-text-primary'
          : 'cursor-pointer text-text-secondary',
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export default Pagination
